import { Navigate } from 'react-router-dom'
import { useAuth, type UserRole } from '@/contexts/AuthContext'
import type { AppPermission } from '@/lib/auth/permissions'
import { hasAllPermissions, hasAnyPermission } from '@/lib/auth/permissions'

interface RoleGuardProps {
  children: React.ReactNode
  allowedRoles?: UserRole[]
  allowedPermissions?: AppPermission[]
  requireAllPermissions?: boolean
  redirectTo?: string
}

/**
 * RoleGuard - Protects routes based on user role
 * If user's role is not in allowedRoles, redirects to appropriate dashboard
 */
export default function RoleGuard({
  children,
  allowedRoles,
  allowedPermissions,
  requireAllPermissions = false,
  redirectTo,
}: RoleGuardProps) {
  const { user } = useAuth()

  // If no user yet, let ProtectedRoute handle it
  if (!user) {
    return <>{children}</>
  }

  const hasRoleAccess = !allowedRoles || allowedRoles.includes(user.role)
  const hasPermissionAccess = !allowedPermissions || (
    requireAllPermissions
      ? hasAllPermissions(user, allowedPermissions)
      : hasAnyPermission(user, allowedPermissions)
  )
  const isPermissionDriven = Boolean(allowedPermissions && allowedPermissions.length > 0)

  if ((isPermissionDriven && !hasPermissionAccess) || (!isPermissionDriven && !hasRoleAccess)) {
    // Redirect to specified path or role-appropriate dashboard
    const defaultRedirect = user.role === 'caregiver' ? '/my-dashboard' : '/dashboard'
    return <Navigate to={redirectTo || defaultRedirect} replace />
  }

  return <>{children}</>
}
